import React from "react";
import { motion } from "framer-motion";
import ApexChart from "react-apexcharts";
import { TrendingUp, TrendingDown, Minus, ListTodo } from "lucide-react";
import WellnessTasksList from "components/wellness-tasks-list/wellness-tasks-list";

const WellnessScoreCard = ({
  title,
  icon: Icon,
  color,
  score,
  trend = [],
  summary,
  improvementPlan,
  colorClass,
  tasks,
  onCompleteTask,
}) => {
  // Calculate trend direction
  const getTrendDirection = () => {
    if (trend.length < 2) return "neutral";
    const latest = trend[0];
    const previous = trend[1];
    if (latest > previous) return "up";
    if (latest < previous) return "down";
    return "neutral";
  };

  const trendDirection = getTrendDirection();

  // Sparkline chart options
  const sparklineOptions = {
    chart: {
      type: "area",
      height: 50,
      sparkline: { enabled: true },
      toolbar: { show: false },
      background: "transparent",
      parentHeightOffset: 0,
    },
    grid: {
      padding: {
        top: 10,
        bottom: 0,
      },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    colors: [color],
    tooltip: {
      enabled: true,
      theme: "dark",
      x: { show: false },
    },
  };

  const sparklineSeries = [
    {
      name: title,
      data: trend.slice().reverse(), // Reverse to show oldest to newest
    },
  ];

  // Round score to one decimal place for display (0-10 scale)
  const displayScore = Math.round(score * 10) / 10;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-gray-600 transition-all h-full flex flex-col"
    >
      {/* Header with icon and title */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl bg-gradient-to-r ${colorClass} flex items-center justify-center`}
          >
            <Icon size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>

        {/* Trend indicator */}
        <div className="flex items-center gap-1">
          {trendDirection === "up" && (
            <TrendingUp size={16} className="text-green-400" />
          )}
          {trendDirection === "down" && (
            <TrendingDown size={16} className="text-red-400" />
          )}
          {trendDirection === "neutral" && (
            <Minus size={16} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Score and Sparkline Row */}
      <div className="flex items-center gap-4 mb-4 overflow-visible">
        {/* Circular Score */}
        <div className="relative w-16 h-16 flex-shrink-0">
          <svg className="transform -rotate-90 w-16 h-16">
            <circle
              className="text-gray-700"
              strokeWidth="4"
              stroke="currentColor"
              fill="transparent"
              r="28"
              cx="32"
              cy="32"
            />
            <circle
              style={{ color }}
              strokeWidth="4"
              strokeDasharray={`${displayScore * 17.6} 176`}
              strokeLinecap="round"
              stroke="currentColor"
              fill="transparent"
              r="28"
              cx="32"
              cy="32"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-white">
            {displayScore}
          </span>
        </div>

        {/* Sparkline Chart */}
        <div className="flex-1 h-12 overflow-visible">
          {trend.length > 1 ? (
            <ApexChart
              options={sparklineOptions}
              series={sparklineSeries}
              type="area"
              height={50}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
              Not enough data for trend
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <p className="text-gray-400 text-sm mb-4 min-h-[40px]">{summary || ""}</p>

      {/* Tasks Section */}
      {tasks && tasks.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <ListTodo size={14} />
            Your Tasks
          </h4>
          <WellnessTasksList
            tasks={tasks}
            onComplete={onCompleteTask}
            color={color}
          />
        </div>
      )}

      {/* Improvement Plan */}
      <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50 mt-auto">
        <h4 className="text-sm font-medium text-gray-300 mb-2">
          Improvement Plan
        </h4>
        <p className="text-gray-400 text-sm">{improvementPlan || "No plan available yet."}</p>
      </div>
    </motion.div>
  );
};

export default WellnessScoreCard;
